import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Tag, statusTone, Loader, Empty } from "@/components/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Scale, MessageSquare, Gavel } from "lucide-react";

const TYPES = ["No-show", "Late arrival", "Service quality", "Payment issue", "Incorrect assignment", "Cancellation", "Professional conduct", "Safety", "Credential dispute"];

export default function Disputes() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);

  const load = () => api.get("/disputes").then((r) => setItems(r.data.disputes)).catch(() => setItems(false));
  useEffect(() => { load(); }, []);

  return (
    <div className="fade-up">
      <PageHeader overline="Dispute centre" title="Disputes"
        subtitle="Raise and resolve no-show, payment and conduct issues with evidence and responses."
        actions={<CreateDispute onCreated={load} />} testid="disputes-page" />

      {items === null ? <Loader /> : items.length === 0 ? <Empty label="No disputes raised" /> : (
        <div className="space-y-3">
          {items.map((d) => (
            <button key={d.id} onClick={() => setActive(d)} data-testid={`dispute-${d.id}`}
              className="w-full text-left krida-card p-5 hover:border-white/15 transition-colors">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-krida-crimson/10 grid place-items-center"><Gavel className="w-5 h-5 text-krida-crimson" /></div>
                  <div>
                    <div className="font-semibold">{d.subject}</div>
                    <div className="text-xs text-slate-500">{d.type} · {d.raised_by_name} → {d.against_name}</div>
                  </div>
                </div>
                <Tag tone={statusTone(d.status)}>{d.status}</Tag>
              </div>
            </button>
          ))}
        </div>
      )}
      {active && <DisputeDetail dispute={active} me={user} onClose={() => setActive(null)} onChange={load} />}
    </div>
  );
}

function CreateDispute({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "Payment issue", subject: "", description: "", evidence: "", against_name: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.subject || !f.against_name) { toast.error("Subject and against-party required"); return; }
    try { await api.post("/disputes", f); toast.success("Dispute raised"); setOpen(false); onCreated(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="create-dispute-btn" className="px-4 py-2.5 rounded-full bg-krida-crimson hover:bg-krida-crimsonDark text-krida-bg font-semibold text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Raise dispute</button>
      </DialogTrigger>
      <DialogContent className="bg-krida-card border-white/10 text-slate-100">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Raise a dispute</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="overline text-slate-500 block mb-1">Type</label>
            <Select value={f.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="dispute-type" className="bg-black/40 border-white/10 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-krida-card border-white/10 text-slate-100 max-h-64">{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select></div>
          <Inp label="Against (name)" value={f.against_name} onChange={(v) => set("against_name", v)} testid="dispute-against" />
          <Inp label="Subject" value={f.subject} onChange={(v) => set("subject", v)} testid="dispute-subject" />
          <div><label className="overline text-slate-500 block mb-1">Description</label>
            <textarea data-testid="dispute-desc" rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm outline-none resize-none" /></div>
          <Inp label="Evidence (reference)" value={f.evidence} onChange={(v) => set("evidence", v)} />
          <button onClick={submit} data-testid="dispute-submit" className="w-full py-3 rounded-full bg-krida-crimson hover:bg-krida-crimsonDark text-krida-bg font-semibold">Submit dispute</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DisputeDetail({ dispute, me, onClose, onChange }) {
  const [d, setD] = useState(dispute);
  const [text, setText] = useState("");
  const canManage = me.role === "admin" || me.role === "organisation";

  const respond = async () => {
    if (!text.trim()) return;
    await api.post(`/disputes/${d.id}/respond`, { text });
    const fresh = (await api.get(`/disputes/${d.id}`)).data;
    setD(fresh); setText(""); onChange();
  };
  const setStatus = async (status) => {
    const resolution = status === "Resolved" ? "Resolved by reviewer" : null;
    await api.put(`/disputes/${d.id}/status`, { status, resolution });
    setD({ ...d, status, resolution }); onChange(); toast.success(`Marked ${status}`);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-krida-card border-white/10 text-slate-100 max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-2xl flex items-center gap-2"><Scale className="w-5 h-5 text-krida-crimson" /> {d.subject}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="flex items-center gap-2"><Tag tone="cobalt">{d.type}</Tag><Tag tone={statusTone(d.status)}>{d.status}</Tag></div>
          <div className="text-sm text-slate-300">{d.description}</div>
          <div className="text-xs text-slate-500">{d.raised_by_name} → {d.against_name} · Evidence: {d.evidence || "—"}</div>
          <div className="border-t border-white/5 pt-3">
            <div className="overline text-slate-500 mb-2 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Responses</div>
            {(d.responses || []).length === 0 && <div className="text-xs text-slate-500">No responses yet.</div>}
            <div className="space-y-2">
              {(d.responses || []).map((r, i) => (
                <div key={i} className="text-sm border-l-2 border-krida-crimson/40 pl-3"><span className="font-semibold">{r.by}</span><div className="text-slate-400 text-xs">{r.text}</div></div>
              ))}
            </div>
          </div>
          {d.resolution && <div className="text-sm text-krida-emerald">Resolution: {d.resolution}</div>}
          {d.status !== "Resolved" && (
            <div className="flex gap-2">
              <input data-testid="dispute-respond-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a response…" className="flex-1 px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm outline-none" />
              <button onClick={respond} data-testid="dispute-respond-btn" className="px-4 rounded-xl bg-white/10 hover:bg-white/15 text-sm font-semibold">Send</button>
            </div>
          )}
          {canManage && d.status !== "Resolved" && (
            <div className="flex gap-2">
              <button onClick={() => setStatus("Under Review")} className="flex-1 py-2 rounded-xl bg-krida-amber/15 text-krida-amber text-sm font-semibold">Under Review</button>
              <button onClick={() => setStatus("Resolved")} data-testid="dispute-resolve-btn" className="flex-1 py-2 rounded-xl bg-krida-emerald/15 text-krida-emerald text-sm font-semibold">Resolve</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Inp({ label, value, onChange, testid }) {
  return (<div><label className="overline text-slate-500 block mb-1">{label}</label>
    <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2.5 rounded-xl bg-black/40 border border-white/10 text-sm outline-none" /></div>);
}
